import React from 'react';
import styled from 'styled-components';

import { useCardCarousel } from '../../hooks/useCardCarousel';
import CarouselPips from '../CarouselPips';

import { Eyebrow, Title } from './headings';

type FeatureCardData = {
  key: string;
  image: string;
  title: string;
  description: string;
  mobileDescription?: string;
};

const FEATURE_CARDS: FeatureCardData[] = [
  {
    key: 'sleep',
    image: '/images/sections/expertCommunity/rest-that-actually-works.png',
    title: 'Sleep & relaxation',
    description:
      'When you fall asleep, what got in the way, and the one change to try tonight.',
  },
  {
    key: 'stress',
    image: '/images/sections/expertCommunity/feel-good-in-your-body.png',
    title: 'Stress, anxiety & low mood',
    description:
      'What your hard days have in common - and the small things that steady them.',
  },
  {
    key: 'weight',
    image: '/images/sections/expertCommunity/build-health-from-within.png',
    title: 'Weight management',
    description:
      'Weekly progress, not daily judgement. What’s working, and what to change next.',
  },
  {
    key: 'energy',
    image: '/images/sections/expertCommunity/energy.png',
    title: 'Energy & vitality',
    description:
      'Where your afternoons go flat, and what actually recharges you.',
  },
  {
    key: 'immune',
    image: '/images/sections/expertCommunity/immune.png',
    title: 'Immune & gut health',
    description:
      'Food first: what you’re eating, what’s missing, starting with breakfast.',
  },
  {
    key: 'focus',
    image: '/images/sections/expertCommunity/focus.png',
    title: 'Brain, focus & memory',
    description:
      'When you’re sharpest, what dulls it, and how to plan the day around it.',
  },
];

const SixAreasSection = () => {
  const areas = useCardCarousel();

  return (
    <section>
      <TextContent>
        <Eyebrow>Six areas, six coaches</Eyebrow>
        <SixAreasTitle>What do you want to work on?</SixAreasTitle>
        <Paragraph>
          Pick where to start. Switch whenever you like - same data, different
          lens, and the advice changes with it.
        </Paragraph>
      </TextContent>
      <FeatureCardsRow ref={areas.ref} onPointerDown={areas.hold}>
        {FEATURE_CARDS.map((card) => (
          <FeatureCard key={card.key}>
            <FeatureAvatar>
              <img src={card.image} alt={card.title} width={80} height={80} />
            </FeatureAvatar>
            <FeatureTitle>{card.title}</FeatureTitle>
            <FeatureDescription>
              <DesktopOnly>{card.description}</DesktopOnly>
              <MobileOnly>
                {card.mobileDescription ?? card.description}
              </MobileOnly>
            </FeatureDescription>
          </FeatureCard>
        ))}
      </FeatureCardsRow>
      <CarouselPips
        count={areas.count}
        activeIdx={areas.activePageIndex}
        onSelect={areas.goTo}
      />
    </section>
  );
};

const Paragraph = styled.p`
  font-size: 20px;
  font-weight: 400;
  line-height: 35px;
  color: ${({ theme }) => theme.colors.topicBlue};
  margin: 0 auto 18px;
  max-width: 1079px;
  text-align: center;
  white-space: pre-line;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    white-space: normal;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding: 0 20px;
    margin-bottom: 28px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 16px;
    line-height: 26px;
    opacity: 0.8;
  }
`;

const SixAreasTitle = styled(Title)`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    max-width: 310px;
  }
`;

const TextContent = styled.article`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    white-space: normal;
    align-items: center;
  }
`;

const FeatureCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 440px));
  justify-content: center;
  gap: 40px;
  margin-top: 64px;
  padding: 0 50px;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    grid-template-columns: repeat(2, minmax(0, 440px));
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding-left: 20px;
    gap: 12px;
    margin-top: 32px;
    padding: 0 20px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const FeatureCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 21px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  border: 2px solid rgb(65 78 113 / 20%);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 5px 20px rgb(0 0 0 / 5%);

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: calc(100vw - 76px);
    min-height: 240px;
    padding: 24px 8px 24px 24px;
    flex-shrink: 0;
    scroll-snap-align: start;
  }
`;

const FeatureAvatar = styled.div`
  box-sizing: border-box;
  width: 100px;
  height: 100px;
  padding: 8px;
  margin-bottom: 16px;
  border-radius: 50%;
  border: 2px dashed ${({ theme }) => theme.colors.secondary};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 73px;
    height: 73px;
    padding: 6px;
  }
`;

const FeatureTitle = styled.h5`
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.topicBlue};
  margin: 0 0 8px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 16px;
  }
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 27px;
  color: ${({ theme }) => theme.colors.topicBlue};
  margin: 0;
  white-space: pre-line;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 14px;
    line-height: 23px;
  }
`;

const DesktopOnly = styled.span`
  font: inherit;
  color: inherit;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: none;
  }
`;

const MobileOnly = styled.span`
  display: none;
  font: inherit;
  color: inherit;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: inline;
  }
`;

export default SixAreasSection;
